import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import TenantPublicPage from "./page";
import NotFound from "@/pages/NotFound";
import LoadingState from "@/components/base/LoadingState";

/*
 * PublicResolver — route wildcard handler.
 * When nginx is configured for custom domains, requests arrive at "*" with
 * the tenant hostname in window.location.hostname. This resolver attempts to
 * find the tenant by slug first (if present), then by active custom_domain.
 * If no match → renders <NotFound /> directly without a full page DB hit.
 */
type ResolveResult =
  | { status: "loading" }
  | { status: "resolved"; tenantId: string; slug: string }
  | { status: "notfound" };

function isExcludedHostname(hostname: string): boolean {
  // Add main app domains here. These will always route to the app, never to a tenant.
  const excluded = ["pixory.ai", "www.pixory.ai", "localhost", "127.0.0.1"];
  return excluded.some((h) => hostname === h || hostname.endsWith(`.${h}`));
}

export default function PublicResolver() {
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const [result, setResult] = useState<ResolveResult>({ status: "loading" });

  useEffect(() => {
    const resolve = async () => {
      /* 1) If we have a slug, trust it and resolve by slug */
      if (tenantSlug) {
        const { data } = await supabase
          .from("saas_tenants")
          .select("id, slug, status, custom_domain, custom_domain_enabled, custom_domain_status")
          .eq("slug", tenantSlug)
          .maybeSingle();

        if (data && data.status === "active") {
          setResult({ status: "resolved", tenantId: data.id, slug: data.slug });
        } else {
          setResult({ status: "notfound" });
        }
        return;
      }

      /* 2) No slug — try to resolve by hostname (preparation for nginx custom domains) */
      const hostname = window.location.hostname;
      if (!hostname || isExcludedHostname(hostname)) {
        setResult({ status: "notfound" });
        return;
      }

      const { data } = await supabase
        .from("saas_tenants")
        .select("id, slug, status")
        .eq("custom_domain", hostname)
        .eq("custom_domain_enabled", true)
        .eq("custom_domain_status", "active")
        .maybeSingle();

      if (data && data.status === "active") {
        setResult({ status: "resolved", tenantId: data.id, slug: data.slug });
      } else {
        setResult({ status: "notfound" });
      }
    };

    resolve();
  }, [tenantSlug]);

  if (result.status === "loading") {
    return <LoadingState fullPage theme="light" text="Resolving studio…" />;
  }

  if (result.status === "notfound") {
    return <NotFound />;
  }

  /* Pass resolved tenant info so TenantPublicPage can skip its own initial lookup */
  return (
    <TenantPublicPage
      preResolvedTenantId={result.tenantId}
      preResolvedSlug={result.slug}
    />
  );
}